/* eslint-disable react/prop-types */
/* eslint-disable jsx-a11y/label-has-for */
import React, { Component } from 'react';
import bindMethods from 'auto-bind';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Formik, Form, FieldArray } from 'formik';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';

import { generateFormErrors } from '../utils/generateFormErrors';
import { addNewOrg, editExistingOrg } from '../state/orgs';
import { FormInputField } from '../components/form/FormInputField';
import { FormSelectField } from '../components/form/FormSelectField';
import getOrgProductsByOrgId from '../state/selectors/getOrgProductsByOrgId';
import getOrgUsersByOrgId from '../state/selectors/getOrgUsersByOrgId';
import { addProductToOrg, fetchOrgProducts, removeProductFromOrg } from '../state/orgProducts';
import { fetchOrgUsers, removeUserFromOrg, addUserToOrg } from '../state/orgUsers';

const NewOrgForm = styled(Form)`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const SubHeadline = styled.h3`
  color: ${props => props.theme.PRIMARY};
  font-weight: normal;
  font-size: 18px;
  margin-bottom: 10px;
`;

const StyledButton = styled.button`
  border: ${props => `1px solid ${props.theme.PRIMARY}`};
  color: ${props => props.reversed ? props.theme.WHITE : props.theme.PRIMARY};
  padding: '4px 5px';
  outline: none;
  display: flex;
  align-items: center;
  cursor: pointer;
  background: ${props => props.reversed ? props.theme.PRIMARY : props.theme.WHITE};
  font-size: 14px;
  margin-right: ${props => props.padRight ? '5px' : ''};
`;

const StyledSelect = styled.select`
  height: 22px;
  background-position: right 50%;
  padding-left: 5px;
  padding-right: 10px;
  border: ${props => `solid 1px ${props.theme.GREY}`};
  background: transparent;
  border-radius: 1px;
  margin-right: ${props => props.padRight ? '5px' : ''};
`;

class OrgForm extends Component {
  constructor(props) {
    super(props);
    bindMethods(this);

    this.productList = React.createRef();
    this.roleSelectList = React.createRef();
    this.userSelectList = React.createRef();
    this.productSelectList = React.createRef();

    this.state = {
      isEditMode: false,
      addUser: false,
    };
  }

  componentDidMount() {
    const {
      match, products, fetchOrgProducts, fetchOrgUsers,
    } = this.props;

    if ('id' in match.params) {
      if (!products) {
        fetchOrgProducts(match.params.id);
      }
      // TODO: resolve user load state on org users update...
      // RE: SSO-378
      fetchOrgUsers();
      this.setState({ isEditMode: true });
    }
  }

  addProductOrg() {
    const selectListValue = this.productList.current.value;

    if (selectListValue === 'null') {
      return;
    }

    const { addProductToOrg, productList, org } = this.props;
    const selectedProduct = productList.find(
      product => product.id === Number.parseInt(selectListValue),
    );

    addProductToOrg(org.id, selectedProduct);
  }

  addOrgUser() {
    const selectedUserValue = this.userSelectList.current.value;
    const selectedRoleValue = this.roleSelectList.current.value;
    const selectedProductValue = this.productSelectList.current.value;

    if (selectedUserValue === 'null' || selectedRoleValue === 'null' || selectedProductValue === 'null') {
      return;
    }

    const {
      org, usersList, rolesList, productList, addUserToOrg,
    } = this.props;

    const selectedUser = usersList.find(user => user.id === Number.parseInt(selectedUserValue));
    const selectedRole = rolesList.find(role => role.id === Number.parseInt(selectedRoleValue));
    const selectedProduct = productList.find(
      product => product.id === Number.parseInt(selectedProductValue),
    );

    const stub = this.generateStub(org, selectedRole, selectedUser, selectedProduct);
    addUserToOrg(stub);

    this.setState({ addUser: false });
  }

  cancelAddOrgUser() {
    this.setState({ addUser: false });
  }

  generateAddUserForm() {
    const { t } = this.props;
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '5px',
      }}
      >
        <div>
          {this.generateUserSelectList()}
          {this.generateProductSelectList()}
          {this.generateRoleSelectList()}
        </div>
        <div style={{ display: 'flex' }}>
          <StyledButton type="button" onClick={this.addOrgUser} padRight>
            {t('Shared.General.Add')}
          </StyledButton>
          <StyledButton type="button" onClick={this.cancelAddOrgUser} reversed>
            {t('Shared.General.Cancel')}
          </StyledButton>
        </div>
      </div>
    );
  }

  generateProductList() {
    const { productList, products, t } = this.props;

    const assignedProductIds = products.map(p => p.id);
    const availableProducts = productList.filter(
      product => !assignedProductIds.includes(product.id),
    );

    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
        <StyledSelect ref={this.productList}>
          <option value="null">
            {isEmpty(availableProducts) ? t('Pages.Orgs.Form.NoProduct') : t('Pages.Orgs.Form.SelectProduct')}
          </option>
          {availableProducts.map(p => (
            <option key={`${p.name}:${p.id}`} value={p.id}>
              {p.name}
            </option>
          ))}
        </StyledSelect>
        <StyledButton
          type="button"
          onClick={this.addProductOrg}
          disabled={isEmpty(availableProducts)}
        >
          {t('Shared.General.Add')}
        </StyledButton>
      </div>
    );
  }

  generateRoleSelectList() {
    const { rolesList, t } = this.props;

    return (
      <StyledSelect ref={this.roleSelectList}>
        <option value="null">
          {t('Pages.Orgs.Form.SelectRole')}
        </option>
        {rolesList.map(role => (
          <option key={`${role.name}:${role.id}`} value={role.id}>
            {role.name}
          </option>
        ))}
      </StyledSelect>
    );
  }

  generateProductSelectList() {
    const { products, t } = this.props;

    return (
      <StyledSelect ref={this.productSelectList}>
        <option value="null">
          {t('Pages.Orgs.Form.SelectProduct')}
        </option>
        {products.map(product => (
          <option key={`${product.name}:${product.id}`} value={product.id}>
            {product.name}
          </option>
        ))}
      </StyledSelect>
    );
  }

  generateStub(org, role, user, product) {
    return {
      orgId: org.id,
      org: {
        displayName: org.displayName,
        name: org.name,
        status: true,
      },
      roleId: role.id,
      role: {
        name: role.name,
        description: null,
      },
      productId: product.id,
      product: {
        name: product.name,
        description: null,
        status: true,
        url: product.url,
      },
      userId: user.id,
      user: {
        email: user.email,
        userName: user.userName,
        displayName: user.displayName,
        firstName: user.firstName,
        lastName: user.lastName,
        status: true,
      },
      status: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  }

  generateParentOrgOptions() {
    const { org, orgsList } = this.props;

    if (isEmpty(org)) {
      return orgsList;
    }

    return orgsList.filter(o => o.id !== org.id);
  }

  generateUserSelectList() {
    const { users, usersList, t } = this.props;

    const assignedUserIds = users.map(user => user.userId);
    const availableUsers = usersList.filter(user => !assignedUserIds.includes(user.id));

    return (
      <StyledSelect ref={this.userSelectList} padRight>
        <option value="null">
          {isEmpty(availableUsers) ? t('Pages.Orgs.Form.NoUsers') : t('Pages.Orgs.Form.SelectUser')}
        </option>
        {availableUsers.map(user => (
          <option key={`${user.displayName}:${user.id}`} value={user.id}>
            {`${user.displayName} (${user.email})`}
          </option>
        ))}
      </StyledSelect>
    );
  }

  handleAddNewUser() {
    this.setState({ addUser: true });
  }

  removeSelectedProduct(productId) {
    const { org, removeProductFromOrg } = this.props;
    removeProductFromOrg(org.id, productId);
  }

  removeSelectedUserRole(userId) {
    const { org, removeUserFromOrg } = this.props;
    removeUserFromOrg(org.id, userId);
  }

  renderSelectedProducts() {
    const { products, t } = this.props;
    return products.map(p => (
      <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
        <p style={{ fontSize: '14px' }}>{p.name}</p>
        <StyledButton type="button" onClick={this.removeSelectedProduct.bind(null, p.id)}>
          {t('Shared.General.Remove')}
        </StyledButton>
      </div>
    ));
  }

  renderUserRoles() {
    const { users, t } = this.props;

    return (
      <div>
        {users.sort((a, b) => a.user.displayName.localeCompare(b.user.displayName)).map(user => (
          <div
            key={`${user.user.displayName}:${user.userId}:${user.role.name}`}
            style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginRight: '5px',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              fontSize: '14px',
            }}
            >
              <p style={{ textOverflow: 'ellipsis' }}>
                {`${user.user.displayName} [${user.role.name}]`}
              </p>
            </div>
            <StyledButton type="button" onClick={this.removeSelectedUserRole.bind(null, user.userId)}>
              {t('Shared.General.Remove')}
            </StyledButton>
          </div>
        ))}
      </div>
    );
  }

  render() {
    const {
      t,
      history,
      addNewOrg,
      editExistingOrg,
      org,
      bindSubmitForm,
      productList,
      products,
      users,
    } = this.props;

    const { addUser } = this.state;

    if (!productList || !products) {
      return null;
    }

    return (
      <Formik
        initialValues={{
          displayName: org.displayName || '',
          name: org.name || '',
          status: org.status || false,
          parentId: org.parentId || '',
          products,
          users,
        }}
        onSubmit={async (values, { resetForm, setErrors }) => {
          const updatedValues = {
            ...values,
            products,
            users,
          };

          try {
            if (isEmpty(org)) {
              await addNewOrg(updatedValues);
            } else {
              await editExistingOrg(org.id, updatedValues);
            }
            resetForm();
            history.push('/orgs');
          } catch (error) {
            const errorFields = generateFormErrors(error);
            setErrors(errorFields);
          }
        }}
        render={(props) => {
          const { isEditMode } = this.state;
          bindSubmitForm(props.submitForm);

          return (
            <div style={{ display: 'flex' }}>
              <NewOrgForm>
                <FormSelectField
                  inputId="OrgForm.Input.ParentOrganization"
                  label={t('Shared.FormFields.ParentOrganization')}
                  labelId="OrgForm.Label.ParentOrganization"
                  name="parentId"
                  placeholder={t('Shared.Placeholders.ParentOrganization')}
                  options={this.generateParentOrgOptions()}
                  error={props.errors.parentId}
                />

                <FormInputField
                  inputId="OrgForm.Input.DisplayName"
                  label={t('Shared.FormFields.DisplayName')}
                  labelId="OrgForm.Label.DisplayName"
                  name="displayName"
                  placeholder={t('Shared.Placeholders.DisplayName')}
                  error={props.errors.displayName}
                  isRequired
                />

                <FormInputField
                  inputId="OrgForm.Input.Name"
                  label={t('Shared.FormFields.Name')}
                  labelId="OrgForm.Label.Name"
                  name="name"
                  placeholder={t('Shared.Placeholders.Name')}
                  error={props.errors.name}
                  isRequired
                />

                <FormInputField
                  inputId="OrgForm.Input.Status"
                  label={t('Shared.FormFields.Status')}
                  labelId="OrgForm.Label.Status"
                  name="status"
                  placeholder={t('Shared.Placeholders.Status')}
                  type="checkbox"
                  checked={props.values.status}
                  isRequired={!!isEmpty(org)}
                  error={props.errors.status}
                />
              </NewOrgForm>
              {isEditMode && (
                <div style={{ flex: 3, display: 'flex' }}>
                  <div style={{ flex: 1, padding: '0 10px 0 20px' }}>
                    <FieldArray
                      name="products"
                      render={() => (
                        <>
                          <SubHeadline>
                            {t('Pages.Orgs.Headlines.Products')}
                          </SubHeadline>
                          {this.generateProductList()}
                          {this.renderSelectedProducts()}
                        </>
                      )}
                    />
                  </div>
                  <div style={{ flex: 2, padding: '0 20px' }}>
                    <FieldArray
                      name="users"
                      render={() => (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <SubHeadline>
                              {t('Pages.Orgs.Headlines.UserRoles')}
                            </SubHeadline>
                            <StyledButton
                              type="button"
                              onClick={this.handleAddNewUser}
                              disabled={addUser}
                            >
                              {t('Pages.Orgs.Form.NewUser')}
                            </StyledButton>
                          </div>
                          {addUser && this.generateAddUserForm()}
                          {this.renderUserRoles()}
                        </>
                      )}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        }}
      />
    );
  }
}

OrgForm.propTypes = {
  t: PropTypes.func.isRequired, // supplied from withTranslation
  addNewOrg: PropTypes.func.isRequired, // supplied from Redux
  bindSubmitForm: PropTypes.func.isRequired,
  editExistingOrg: PropTypes.func.isRequired, // supplied from Redux
  history: PropTypes.object.isRequired, // supplied from React Router
  org: PropTypes.object,
  addProductToOrg: PropTypes.func.isRequired, // supplied from Redux
  removeProductFromOrg: PropTypes.func.isRequired, // supplied from Redux
  fetchOrgProducts: PropTypes.func.isRequired, // supplied from Redux
  fetchOrgUsers: PropTypes.func.isRequired, // supplied from Redux
  removeUserFromOrg: PropTypes.func.isRequired, // supplied from Redux
  addUserToOrg: PropTypes.func.isRequired, // supplied from Redux
};

OrgForm.defaultProps = {
  org: {},
};

// NOTE (RC 7/19/2019): This is a temporary solution until Users are able to belong to multiple orgs
// when that happens we simply need to delete below function and replace users on LOC#478.
const getUsersNotAssociatedWithOrg = (state) => {
  if (state.users.isAllUsersLoaded && state.orgUsers.isOrgUsersLoaded) {
    const assignedUsers = state.orgUsers.users.map(u => u.userId);
    return state.users.users.filter(u => !assignedUsers.includes(u.id) && u.status);
  }
  return [];
};

const mapStateToProps = (state, props) => ({
  productList: state.products.products,
  products: getOrgProductsByOrgId(state, isEmpty(props.org) ? null : props.org.id),
  usersList: getUsersNotAssociatedWithOrg(state),
  users: getOrgUsersByOrgId(state, isEmpty(props.org) ? null : props.org.id),
  rolesList: state.roles.roles,
  orgsList: state.orgs.orgs,
});

const mapDispatchToProps = {
  addNewOrg,
  editExistingOrg,
  fetchOrgProducts,
  addProductToOrg,
  removeProductFromOrg,
  fetchOrgUsers,
  removeUserFromOrg,
  addUserToOrg,
};

const TranslatedComponent = withTranslation()(OrgForm);
const RouterComponent = withRouter(TranslatedComponent);
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(RouterComponent);
